/**
 * The Chats list's Workspace tabs for the open Account (ADR 0027, glossary):
 * Activity, Suggestions and Active chats are Workspace's own (its filters,
 * dismissals, Feed settings and Skip, its Active chats rule), narrowed to
 * this Account, drawn as chat rows. A row opens the fan's chat here.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useMediaSrc } from '../library/useMediaSrc'
import { activityItems, activitySettingsFrom } from '../workspace/activity'
import { activityCard, feedCards, type CardContext } from '../workspace/cards'
import { activeChatGroups, applySend, isFanReplied, windowHours, type ActiveChat, type ActiveWindow, type SendEvent } from '../workspace/chats'
import { useDismissed } from '../workspace/dismissed'
import { feedSettingsFrom, type FeedItem } from '../workspace/feed'
import { useFeed } from '../workspace/useFeed'
import { useWorkspace } from '../workspace/WorkspaceProvider'
import { avatarOf, formatChatTime, type Raw } from './chatRows'
import { useChatsDeps, type LiveEvent } from './ChatsDeps'
import { GroupHead, TabEmpty as Empty, TabRows as SharedRows, type RowView as SharedRow } from './TabRow'

/** Active chats are read again this often (as Workspace does); live events keep them current between. */
const ACTIVE_RELOAD_MS = 120_000
/** Waiting on fan starts folded past this many chats. */
const WAITING_FOLD = 10

export interface TabViewProps {
  accountId: string
  activeFanId: string | null
  onOpen: (fanId: string, name: string) => void
  /** Names and avatars the list already knows (users/list, our DB), by fan id. */
  fans: Map<string, Raw>
  /** Ask for names of fans the list does not know yet. */
  name: (fanIds: string[]) => void
}

/** A row as these tabs build it; the avatar is an OnlyFans URL loaded through the Library's media loader. */
type RowView = Omit<SharedRow, 'avatar'> & { avatar: string | null }

/** The person's name for a fan: what the row came with, else what the list knows, else their id. */
function useNamer(fans: Map<string, Raw>, name: (ids: string[]) => void, ids: string[]) {
  const missing = ids.filter((id) => !fans.has(id)).join(',')
  useEffect(() => {
    if (missing) name(missing.split(','))
  }, [missing, name])
  return useCallback(
    (fanId: string, given?: string | null, avatar?: string | null) => {
      const u = fans.get(fanId)
      const fromList = String(u?.displayName || u?.name || u?.username || '')
      return { name: given || fromList || `Fan ${fanId}`, avatar: avatar || (u ? avatarOf(u) : null) }
    },
    [fans],
  )
}

function useCardContext(): CardContext {
  const { t } = useTranslation('workspace')
  const { t: tc } = useTranslation('chat')
  const { now } = useChatsDeps()
  const ws = useWorkspace()
  const nowMs = now().getTime()
  const accounts = useMemo(() => new Map(ws.accounts.map((a) => [a.account_id, a])), [ws.accounts])
  return useMemo(() => ({ accounts, nowMs, t, tc }), [accounts, nowMs, t, tc])
}

function useTime() {
  const { t, i18n } = useTranslation('chats')
  const { now } = useChatsDeps()
  return (iso: string | null | undefined) => (iso ? formatChatTime(iso, now(), i18n.language, t('yesterday')) : '')
}

// ── Activity ────────────────────────────────────────────────────────────────

export function ActivityTabView({ accountId, activeFanId, onOpen, fans, name }: TabViewProps) {
  const { t } = useTranslation('chats')
  const { history } = useChatsDeps()
  const ws = useWorkspace()
  const ctx = useCardContext()
  const time = useTime()
  const [events, setEvents] = useState<LiveEvent[] | null>(null)
  useEffect(() => history?.(accountId, setEvents), [history, accountId])
  const settings = useMemo(() => activitySettingsFrom(ws.settings.activity), [ws.settings.activity])
  const { dismissed, dismiss } = useDismissed()
  const items = useMemo(
    () => activityItems((events ?? []).filter((e) => e.account_id === accountId), settings, dismissed, ctx.nowMs),
    [events, accountId, settings, dismissed, ctx.nowMs],
  )
  const named = useNamer(fans, name, useMemo(() => [...new Set(items.filter((i) => !i.event.payload.fan_name).map((i) => i.event.fan_id))], [items]))
  const dismissOne = useCallback((id: string) => dismiss([id]), [dismiss])
  const rows = items.map((item, i): RowView => {
    const card = activityCard(item, i, ctx, dismissOne)
    const who = named(item.event.fan_id, item.event.payload.fan_name, item.event.payload.fan_avatar)
    return {
      key: item.id,
      fanId: item.event.fan_id,
      name: who.name,
      avatar: who.avatar,
      badge: card.kind ? { icon: card.kind.icon, label: card.kind.label, amount: card.kind.amount, hue: card.kind.hue } : undefined,
      preview: card.sub ?? '',
      time: time(item.event.occurred_at),
      dim: card.dim,
      action: card.action ? { icon: 'x', label: t('builtin.dismiss'), run: card.action.run } : undefined,
    }
  })
  return (
    <TabRows rows={rows} activeFanId={activeFanId} onOpen={onOpen} loading={events === null} empty={<Empty icon="activity" title={t('builtin.activityEmpty')} text={t('builtin.activityEmptyText')} />} />
  )
}

// ── Suggestions ─────────────────────────────────────────────────────────────

export function SuggestionsTabView({ accountId, activeFanId, onOpen, fans, name }: TabViewProps) {
  const { t } = useTranslation('chats')
  const ws = useWorkspace()
  const ctx = useCardContext()
  const time = useTime()
  const settings = useMemo(() => feedSettingsFrom(ws.settings.feed), [ws.settings.feed])
  const scope = useMemo(() => [accountId], [accountId])
  const feed = useFeed({ enabled: ws.enabled && ws.prefsLoaded, settings, scope, onEvent: ws.onEvent, autoRun: false, openKey: activeFanId ? `${accountId}:${activeFanId}` : null })
  const items = useMemo(() => feed.items.filter((it) => it.account_id === accountId), [feed.items, accountId])
  const skip = useCallback((it: FeedItem) => feed.skip(it), [feed])
  const cards = useMemo(() => feedCards(items, settings, ctx, skip), [items, settings, ctx, skip])
  const named = useNamer(fans, name, useMemo(() => items.filter((i) => !i.fan_name && !i.fan_username).map((i) => i.fan_id), [items]))
  const rows = cards.map((card, i): RowView => {
    const it = items[i]!
    const who = named(it.fan_id, it.fan_name || it.fan_username, it.fan_avatar)
    return {
      key: card.key,
      fanId: it.fan_id,
      name: who.name,
      avatar: who.avatar,
      badge: card.spent ? { icon: 'dollar-sign', label: t('builtin.spent'), amount: card.spent, tone: 'accent' } : undefined,
      preview: card.sub ?? '',
      time: time(it.last_chat_at),
      action: { icon: 'x', label: t('builtin.skip'), run: () => skip(it) },
    }
  })
  // Also while the rest of the first page is on its way: the list shows a spinner under its rows.
  const loading = !feed.loaded || feed.loading
  const configured = ws.settings.feed != null
  return (
    <TabRows
      rows={rows}
      activeFanId={activeFanId}
      onOpen={onOpen}
      loading={loading}
      empty={
        feed.error ? (
          <Empty icon="alert-circle" title={t('builtin.suggestionsError')} />
        ) : (
          <Empty icon="zap" title={t(configured ? 'builtin.suggestionsEmpty' : 'builtin.suggestionsSetup')} text={configured ? undefined : t('builtin.suggestionsSetupText')}>
            <Link className="chat-tab-empty-link" to="/workspace">
              {t('builtin.openWorkspace')}
            </Link>
          </Empty>
        )
      }
    />
  )
}

// ── Active chats ────────────────────────────────────────────────────────────

/** A live event's effect on an Active chat we already list (the CRM's chat state, kept current between reloads). */
function applyLive(chats: ActiveChat[], ev: LiveEvent, myId: string | null): ActiveChat[] {
  if (ev.kind === 'team_sent' && myId && ev.payload.operator_id === myId) return applySend(chats, ev as unknown as SendEvent, myId)
  const i = chats.findIndex((c) => c.fan_id === ev.fan_id)
  if (i < 0) return chats
  const c = chats[i]!
  let next: ActiveChat | null = null
  if (ev.kind === 'fan_message') next = { ...c, last_fan_msg_at: ev.occurred_at, last_fan_snippet: ev.payload.snippet ?? c.last_fan_snippet }
  else if (ev.kind === 'ai_sent') next = { ...c, last_out_at: ev.occurred_at, last_out_by: 'ai' }
  else if (ev.kind === 'team_sent') next = { ...c, last_out_at: ev.occurred_at, last_out_by: String(ev.payload.operator_id ?? 'team') }
  if (!next) return chats
  const out = chats.slice()
  out[i] = next
  return out
}

export function ActiveChatsTabView({ accountId, activeFanId, onOpen, fans, name }: TabViewProps) {
  const { t } = useTranslation('chats')
  const { local, subscribe, now } = useChatsDeps()
  const ws = useWorkspace()
  const time = useTime()
  const activeWindow: ActiveWindow = (ws.settings.activeWindow as ActiveWindow) || 'today'
  const myId = ws.me?.id ?? null
  const [chats, setChats] = useState<ActiveChat[] | null>(null)
  const [unfolded, setUnfolded] = useState(false)

  useEffect(() => {
    let alive = true
    const load = () => {
      const hours = Math.max(0.05, Math.round(windowHours(activeWindow, now().getTime()) * 100) / 100)
      local<{ chats?: ActiveChat[] }>(`/v1/workspace/active?window_hours=${hours}`, {}, 'GET').then(
        (d) => alive && setChats((d?.chats ?? []).filter((c) => c.account_id === accountId)),
        () => alive && setChats((prev) => prev ?? []),
      )
    }
    load()
    const timer = window.setInterval(load, ACTIVE_RELOAD_MS)
    return () => {
      alive = false
      window.clearInterval(timer)
    }
  }, [local, accountId, activeWindow, now])

  useEffect(() => subscribe(accountId, (ev) => setChats((prev) => (prev && ev.account_id === accountId ? applyLive(prev, ev, myId) : prev))), [subscribe, accountId, myId])

  const groups = useMemo(() => activeChatGroups(chats ?? [], { window: activeWindow, nowMs: now().getTime() }), [chats, activeWindow, now])
  const named = useNamer(fans, name, useMemo(() => (chats ?? []).filter((c) => !c.fan_name).map((c) => c.fan_id), [chats]))
  const row = (c: ActiveChat): RowView => {
    const who = named(c.fan_id, c.fan_name, c.fan_avatar)
    const replied = isFanReplied(c)
    const aiAfter = !replied && c.last_out_by === 'ai'
    return {
      key: c.fan_id,
      fanId: c.fan_id,
      name: who.name,
      avatar: who.avatar,
      badge: replied
        ? { icon: 'message-circle', label: t('builtin.fanReplied'), tone: 'accent' }
        : aiAfter
          ? { icon: 'cpu', label: t('builtin.aiReplied'), tone: 'muted' }
          : { icon: 'clock', label: t('builtin.waiting'), tone: 'muted' },
      preview: c.last_fan_snippet ? `${t('builtin.fan')}: ${c.last_fan_snippet}` : t(aiAfter ? 'builtin.aiAfterYou' : 'builtin.youWroteLast'),
      time: time(replied ? c.last_fan_msg_at : (c.last_out_at ?? c.my_last_at)),
    }
  }
  const waiting = unfolded || groups.waiting.length <= WAITING_FOLD ? groups.waiting : groups.waiting.slice(0, WAITING_FOLD)
  const empty = <Empty icon="messages-square" title={t('builtin.activeEmpty')} text={t('builtin.activeEmptyText')} />
  if (chats === null) return <TabRows rows={[]} activeFanId={activeFanId} onOpen={onOpen} loading empty={empty} />
  if (!groups.replied.length && !groups.waiting.length) return <TabRows rows={[]} activeFanId={activeFanId} onOpen={onOpen} loading={false} empty={empty} />
  return (
    <>
      <GroupHead label={t('builtin.repliedGroup', { count: groups.replied.length })} />
      <TabRows rows={groups.replied.map(row)} activeFanId={activeFanId} onOpen={onOpen} loading={false} empty={<div className="chat-tab-group-empty">{t('builtin.noneReplied')}</div>} />
      <GroupHead label={t('builtin.waitingGroup', { count: groups.waiting.length })} />
      <TabRows rows={waiting.map(row)} activeFanId={activeFanId} onOpen={onOpen} loading={false} empty={<div className="chat-tab-group-empty">{t('builtin.noneWaiting')}</div>} />
      {waiting.length < groups.waiting.length && (
        <button type="button" className="chat-list-older" onClick={() => setUnfolded(true)}>
          {t('builtin.showMore', { count: groups.waiting.length - waiting.length })}
        </button>
      )}
    </>
  )
}

// ── Rows ────────────────────────────────────────────────────────────────────

function TabRows({ rows, activeFanId, onOpen, loading, empty }: { rows: RowView[]; activeFanId: string | null; onOpen: (fanId: string, name: string) => void; loading: boolean; empty: ReactNode }) {
  const shared = rows.map((r): SharedRow => ({ ...r, avatar: <RowAvatar url={r.avatar} name={r.name} />, avatarKey: `${r.avatar ?? ''}|${r.name}` }))
  return <SharedRows rows={shared} isActive={(r) => r.fanId === activeFanId} onOpen={(r) => onOpen(r.fanId, r.name)} loading={loading} empty={empty} />
}

function RowAvatar({ url, name }: { url: string | null; name: string }) {
  const avatar = useMediaSrc(url ?? '')
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  return (
    <span className="chat-avatar" ref={avatar.ref as React.RefObject<HTMLSpanElement>}>
      {avatar.src ? <img src={avatar.src} alt="" loading="lazy" /> : <span>{initials}</span>}
    </span>
  )
}
